/**
 * Rax Activity Heatmap Component
 *
 * GitHub-style contribution graph showing agent activity over time.
 * Helps users visualize when their agent is most active.
 *
 * Features:
 * - 12 weeks of daily activity squares
 * - Color intensity based on activity level (messages, actions, cost)
 * - Hover tooltips showing date + detailed stats
 * - Streak tracking with flame animation
 * - Fully responsive design
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { raxApi } from "../../api/rax-api.js";
import { raxTheme } from "../styles/rax-theme.js";

interface DayActivity {
  date: string; // YYYY-MM-DD
  count: number;
  level: 0 | 1 | 2 | 3 | 4; // Activity level for coloring
  messages?: number;
  actions?: number;
  costCents?: number;
}

interface ActivityStats {
  totalActions: number;
  activeDays: number;
  currentStreak: number;
  longestStreak: number;
  totalSpendCents: number;
  totalMessages?: number;
  avgActionsPerDay?: number;
  mostActiveDay?: string;
}

interface HeatmapData {
  days: DayActivity[];
  stats: ActivityStats;
  weeks: number;
}

@customElement("rax-activity-heatmap")
export class RaxActivityHeatmap extends LitElement {
  static override styles = [
    raxTheme,
    css`
      :host {
        display: block;
      }

      .heatmap-container {
        padding: var(--space-4);
        background: var(--bg-elevated);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-lg);
      }

      .heatmap-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-4);
        flex-wrap: wrap;
        gap: var(--space-2);
      }

      .heatmap-title {
        font-size: var(--font-md);
        font-weight: 600;
        color: var(--text-primary);
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .streak-badge {
        display: flex;
        align-items: center;
        gap: var(--space-1);
        padding: var(--space-1) var(--space-3);
        background: rgba(255, 208, 0, 0.15);
        border-radius: var(--radius-full);
        font-size: var(--font-xs);
        font-weight: 600;
        color: var(--rax-yellow);
        transition: all var(--transition-fast);
      }

      .streak-badge.active {
        animation: streakPulse 2s ease-in-out infinite;
      }

      @keyframes streakPulse {
        0%, 100% { box-shadow: 0 0 0 0 rgba(255, 208, 0, 0.3); }
        50% { box-shadow: 0 0 12px 4px rgba(255, 208, 0, 0.2); }
      }

      .streak-badge .flame {
        animation: flicker 0.5s ease-in-out infinite alternate;
      }

      @keyframes flicker {
        0% { transform: scale(1) rotate(-5deg); }
        100% { transform: scale(1.1) rotate(5deg); }
      }

      .streak-badge.inactive {
        background: rgba(156, 163, 175, 0.15);
        color: var(--text-tertiary);
      }

      /* Stats row - responsive grid */
      .stats-row {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
        gap: var(--space-3);
        margin-bottom: var(--space-4);
        padding: var(--space-3);
        background: var(--bg-primary);
        border-radius: var(--radius-md);
      }

      .stat-item {
        display: flex;
        flex-direction: column;
        gap: 2px;
        text-align: center;
        padding: var(--space-2);
        border-radius: var(--radius-sm);
        transition: background var(--transition-fast);
      }

      .stat-item:hover {
        background: var(--bg-hover);
      }

      .stat-value {
        font-size: var(--font-lg);
        font-weight: 700;
        color: var(--text-primary);
      }

      .stat-value.highlight {
        color: var(--rax-yellow);
      }

      .stat-label {
        font-size: var(--font-xs);
        color: var(--text-tertiary);
      }

      /* Heatmap wrapper for horizontal scroll on small screens */
      .heatmap-wrapper {
        overflow-x: auto;
        overflow-y: hidden;
        padding-bottom: var(--space-2);
        -webkit-overflow-scrolling: touch;
      }

      .heatmap-wrapper::-webkit-scrollbar {
        height: 6px;
      }

      .heatmap-wrapper::-webkit-scrollbar-track {
        background: var(--bg-primary);
        border-radius: 3px;
      }

      .heatmap-wrapper::-webkit-scrollbar-thumb {
        background: var(--border-color);
        border-radius: 3px;
      }

      .heatmap-wrapper::-webkit-scrollbar-thumb:hover {
        background: var(--rax-yellow);
      }

      /* Heatmap grid */
      .heatmap-grid {
        display: flex;
        gap: 3px;
        min-width: max-content;
      }

      .heatmap-week {
        display: flex;
        flex-direction: column;
        gap: 3px;
      }

      .heatmap-day {
        width: 12px;
        height: 12px;
        border-radius: 2px;
        cursor: pointer;
        transition: all 0.15s ease;
        position: relative;
      }

      .heatmap-day:hover {
        transform: scale(1.4);
        z-index: 10;
        box-shadow: 0 0 8px rgba(255, 208, 0, 0.4);
      }

      .heatmap-day.level-0 {
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
      }

      .heatmap-day.level-1 {
        background: rgba(255, 208, 0, 0.2);
        border: 1px solid rgba(255, 208, 0, 0.3);
      }

      .heatmap-day.level-2 {
        background: rgba(255, 208, 0, 0.4);
        border: 1px solid rgba(255, 208, 0, 0.5);
      }

      .heatmap-day.level-3 {
        background: rgba(255, 208, 0, 0.65);
        border: 1px solid rgba(255, 208, 0, 0.75);
      }

      .heatmap-day.level-4 {
        background: var(--rax-yellow);
        border: 1px solid var(--rax-gold);
        box-shadow: 0 0 4px rgba(255, 208, 0, 0.3);
      }

      .heatmap-day.today {
        outline: 2px solid var(--rax-yellow);
        outline-offset: 1px;
      }

      .heatmap-day.future {
        background: transparent;
        border: 1px dashed var(--border-color);
        opacity: 0.5;
      }

      /* Legend */
      .legend {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-2);
        margin-top: var(--space-3);
        font-size: var(--font-xs);
        color: var(--text-tertiary);
        flex-wrap: wrap;
      }

      .legend-scale-container {
        display: flex;
        align-items: center;
        gap: var(--space-2);
      }

      .legend-label {
        margin-right: var(--space-1);
      }

      .legend-scale {
        display: flex;
        gap: 3px;
      }

      .legend-day {
        width: 12px;
        height: 12px;
        border-radius: 2px;
      }

      .legend-info {
        font-size: 10px;
        color: var(--text-tertiary);
      }

      /* Day labels */
      .day-labels {
        display: flex;
        flex-direction: column;
        gap: 3px;
        margin-right: var(--space-2);
        font-size: 9px;
        color: var(--text-tertiary);
        flex-shrink: 0;
      }

      .day-label {
        height: 12px;
        display: flex;
        align-items: center;
      }

      /* Month labels */
      .month-labels {
        display: flex;
        gap: 3px;
        margin-left: 24px;
        margin-bottom: var(--space-1);
        font-size: 9px;
        color: var(--text-tertiary);
      }

      .month-label {
        min-width: 12px;
      }

      /* Enhanced tooltip */
      .tooltip {
        position: fixed;
        padding: var(--space-3);
        background: var(--bg-secondary);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-md);
        font-size: var(--font-xs);
        color: var(--text-primary);
        pointer-events: none;
        z-index: 1000;
        box-shadow: var(--shadow-lg);
        min-width: 140px;
        transform: translateX(-50%);
      }

      .tooltip-date {
        font-weight: 600;
        margin-bottom: var(--space-2);
        padding-bottom: var(--space-1);
        border-bottom: 1px solid var(--border-color);
        color: var(--rax-yellow);
      }

      .tooltip-stats {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
      }

      .tooltip-stat {
        display: flex;
        justify-content: space-between;
        gap: var(--space-3);
      }

      .tooltip-stat-label {
        color: var(--text-secondary);
      }

      .tooltip-stat-value {
        font-weight: 600;
        color: var(--text-primary);
      }

      .tooltip-no-activity {
        color: var(--text-tertiary);
        font-style: italic;
      }

      .loading {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--space-8);
        color: var(--text-tertiary);
      }

      .empty-state {
        text-align: center;
        padding: var(--space-6);
        color: var(--text-tertiary);
        font-size: var(--font-sm);
      }

      .empty-state-icon {
        font-size: 32px;
        margin-bottom: var(--space-2);
        opacity: 0.5;
      }

      /* Responsive adjustments */
      @media (max-width: 600px) {
        .heatmap-container {
          padding: var(--space-3);
        }

        .stats-row {
          grid-template-columns: repeat(2, 1fr);
        }

        .heatmap-day {
          width: 10px;
          height: 10px;
        }

        .legend-day {
          width: 10px;
          height: 10px;
        }

        .day-label {
          height: 10px;
        }
      }
    `,
  ];

  @property({ type: Number })
  weeks = 12;

  @state()
  private data: HeatmapData | null = null;

  @state()
  private loading = true;

  @state()
  private tooltip: {
    x: number;
    y: number;
    day: DayActivity;
  } | null = null;

  override async connectedCallback() {
    super.connectedCallback();
    await this.loadData();
  }

  private async loadData() {
    this.loading = true;
    try {
      const res = await raxApi.request<HeatmapData>(
        "GET",
        `/api/rax/activity/heatmap?weeks=${this.weeks}`,
      );
      if (res.success && res.data) {
        this.data = res.data;
      } else {
        // Show empty state - no mock data
        this.data = null;
      }
    } catch {
      // Show empty state - no mock data
      this.data = null;
    } finally {
      this.loading = false;
    }
  }

  override render() {
    if (this.loading) {
      return html`
        <div class="heatmap-container">
          <div class="loading">Loading activity...</div>
        </div>
      `;
    }

    if (!this.data || this.data.days.length === 0) {
      return html`
        <div class="heatmap-container">
          <div class="empty-state">
            <div class="empty-state-icon">📊</div>
            <div>No activity data yet</div>
            <div style="margin-top: var(--space-1); font-size: var(--font-xs);">
              Start using your agent to see activity here.
            </div>
          </div>
        </div>
      `;
    }

    const { stats } = this.data;
    const streakActive = stats.currentStreak > 0;
    const avgPerDay = stats.avgActionsPerDay ?? (stats.activeDays > 0 ? Math.round(stats.totalActions / stats.activeDays) : 0);

    return html`
      <div class="heatmap-container">
        <div class="heatmap-header">
          <div class="heatmap-title">
            <span>Activity</span>
            <span style="font-weight: 400; color: var(--text-secondary); font-size: var(--font-sm);">
              Last ${this.weeks} weeks
            </span>
          </div>
          <div class="streak-badge ${streakActive ? "active" : "inactive"}">
            ${streakActive ? html`<span class="flame">🔥</span>` : "💤"}
            ${streakActive ? `${stats.currentStreak} day streak` : "No current streak"}
          </div>
        </div>

        <div class="stats-row">
          <div class="stat-item">
            <div class="stat-value highlight">${stats.activeDays}</div>
            <div class="stat-label">Active days</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">${this.formatNumber(stats.totalActions)}</div>
            <div class="stat-label">Total actions</div>
          </div>
          ${stats.totalMessages !== undefined ? html`
            <div class="stat-item">
              <div class="stat-value">${this.formatNumber(stats.totalMessages)}</div>
              <div class="stat-label">Messages</div>
            </div>
          ` : ""}
          <div class="stat-item">
            <div class="stat-value">${this.formatCurrency(stats.totalSpendCents)}</div>
            <div class="stat-label">Total spend</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">${avgPerDay}</div>
            <div class="stat-label">Avg/day</div>
          </div>
          <div class="stat-item">
            <div class="stat-value highlight">${stats.longestStreak}</div>
            <div class="stat-label">Best streak</div>
          </div>
        </div>

        ${this.renderMonthLabels()}

        <div class="heatmap-wrapper">
          <div style="display: flex;">
            <div class="day-labels">
              <div class="day-label"></div>
              <div class="day-label">Mon</div>
              <div class="day-label"></div>
              <div class="day-label">Wed</div>
              <div class="day-label"></div>
              <div class="day-label">Fri</div>
              <div class="day-label"></div>
            </div>
            <div class="heatmap-grid">
              ${this.renderWeeks()}
            </div>
          </div>
        </div>

        <div class="legend">
          <div class="legend-scale-container">
            <span class="legend-label">Less</span>
            <div class="legend-scale">
              <div class="legend-day heatmap-day level-0"></div>
              <div class="legend-day heatmap-day level-1"></div>
              <div class="legend-day heatmap-day level-2"></div>
              <div class="legend-day heatmap-day level-3"></div>
              <div class="legend-day heatmap-day level-4"></div>
            </div>
            <span class="legend-label">More</span>
          </div>
          ${stats.mostActiveDay ? html`
            <span class="legend-info">Most active: ${stats.mostActiveDay}</span>
          ` : ""}
        </div>
      </div>

      ${this.renderTooltip()}
    `;
  }

  private renderTooltip() {
    if (!this.tooltip) return "";

    const { day } = this.tooltip;
    const date = new Date(day.date);
    const dateStr = date.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });

    const hasActivity = day.count > 0;

    return html`
      <div class="tooltip" style="left: ${this.tooltip.x}px; top: ${this.tooltip.y}px;">
        <div class="tooltip-date">${dateStr}</div>
        ${hasActivity ? html`
          <div class="tooltip-stats">
            <div class="tooltip-stat">
              <span class="tooltip-stat-label">Actions</span>
              <span class="tooltip-stat-value">${day.count}</span>
            </div>
            ${day.messages !== undefined ? html`
              <div class="tooltip-stat">
                <span class="tooltip-stat-label">Messages</span>
                <span class="tooltip-stat-value">${day.messages}</span>
              </div>
            ` : ""}
            ${day.costCents !== undefined && day.costCents > 0 ? html`
              <div class="tooltip-stat">
                <span class="tooltip-stat-label">Cost</span>
                <span class="tooltip-stat-value">${this.formatCurrency(day.costCents)}</span>
              </div>
            ` : ""}
          </div>
        ` : html`
          <div class="tooltip-no-activity">No activity</div>
        `}
      </div>
    `;
  }

  private formatCurrency(cents: number): string {
    if (cents < 100) return `$${(cents / 100).toFixed(2)}`;
    if (cents < 10000) return `$${(cents / 100).toFixed(2)}`;
    return `$${(cents / 100).toFixed(0)}`;
  }

  private renderMonthLabels() {
    if (!this.data) return "";

    const months: { name: string; span: number }[] = [];
    let currentMonth = "";
    let span = 0;

    for (let i = 0; i < this.data.days.length; i += 7) {
      const date = new Date(this.data.days[i].date);
      const monthName = date.toLocaleDateString(undefined, { month: "short" });

      if (monthName !== currentMonth) {
        if (currentMonth) {
          months.push({ name: currentMonth, span });
        }
        currentMonth = monthName;
        span = 1;
      } else {
        span++;
      }
    }
    if (currentMonth) {
      months.push({ name: currentMonth, span });
    }

    return html`
      <div class="month-labels">
        ${months.map(
          (m) => html`
          <span class="month-label" style="width: ${m.span * 15}px">${m.name}</span>
        `,
        )}
      </div>
    `;
  }

  private renderWeeks() {
    if (!this.data) return "";

    const weeks: DayActivity[][] = [];
    const days = this.data.days;

    // Group days into weeks
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return weeks.map(
      (week) => html`
      <div class="heatmap-week">
        ${week.map((day) => this.renderDay(day))}
      </div>
    `,
    );
  }

  private renderDay(day: DayActivity) {
    const today = new Date().toISOString().slice(0, 10);
    const isFuture = day.date > today;
    const isToday = day.date === today;

    return html`
      <div
        class="heatmap-day level-${day.level} ${isFuture ? "future" : ""} ${isToday ? "today" : ""}"
        @mouseenter=${(e: MouseEvent) => this.showTooltip(e, day)}
        @mouseleave=${() => this.hideTooltip()}
      ></div>
    `;
  }

  private showTooltip(e: MouseEvent, day: DayActivity) {
    const target = e.target as HTMLElement;
    const rect = target.getBoundingClientRect();

    this.tooltip = {
      x: rect.left + rect.width / 2,
      y: rect.top - 10,
      day,
    };
  }

  private hideTooltip() {
    this.tooltip = null;
  }

  private formatNumber(n: number): string {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return String(n);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-activity-heatmap": RaxActivityHeatmap;
  }
}
